import React, { createContext, useCallback, useContext } from 'react'
import { PageSettingsPatch } from '@storefronts/common-types'
import { LandingPageEditorOverrideContext } from './LandingPageEditorOverrideContext'
import { StagedContentContext } from './StagedContentContext'
import {
    applyPageSettingsPatch,
    pageSettingsFromLandingPage,
} from './agent-chat/agentChatUtils'
import {
    StagedContentHistory,
    useStagedContentHistory,
} from './hooks/useStagedContentHistory'

const defaultHistory: StagedContentHistory = {
    entries: [],
    cursor: 0,
    canUndo: false,
    canRedo: false,
    undo: () => {},
    redo: () => {},
    revertTo: () => {},
    commitChatTurn: () => {},
    resetHistory: () => {},
}

export const StagedContentHistoryContext =
    createContext<StagedContentHistory>(defaultHistory)

export const StagedContentHistoryContextProvider: React.FC<{
    children?: React.ReactNode
}> = ({ children }) => {
    const {
        localStagedSections,
        setLocalStagedSections,
        clearSelectionIfInvalid,
    } = useContext(StagedContentContext)
    const { stagedLandingPage, setStagedLandingPage } = useContext(
        LandingPageEditorOverrideContext,
    )
    const pageSettings = pageSettingsFromLandingPage(stagedLandingPage)
    const onPageSettingsRestored = useCallback(
        (restoredPageSettings: PageSettingsPatch) => {
            setStagedLandingPage((landingPage) => {
                const next = applyPageSettingsPatch(
                    landingPage,
                    restoredPageSettings,
                )
                // History stores full snapshots from pageSettingsFromLandingPage,
                // which omits bannerText when the page has none. applyPageSettingsPatch
                // would leave the current value in place; restore the snapshot exactly.
                if (restoredPageSettings.bannerText === undefined) {
                    delete next.bannerText
                } else {
                    next.bannerText = restoredPageSettings.bannerText
                }
                return next
            })
        },
        [setStagedLandingPage],
    )
    const history = useStagedContentHistory(
        localStagedSections,
        setLocalStagedSections,
        clearSelectionIfInvalid,
        pageSettings,
        onPageSettingsRestored,
    )

    return (
        <StagedContentHistoryContext.Provider value={history}>
            {children}
        </StagedContentHistoryContext.Provider>
    )
}
